/**
 * Modal de criação de template (Brief 4 · Tela 3 · PR2 · gate
 * `message-template-manage`).
 *
 * Coleta o mínimo (nome Meta-rules, categoria, body obrigatório) e cria um DRAFT
 * via POST /v1/templates — o ajuste fino (header/footer/botões/submissão) acontece
 * no editor logo após (o `onCreated` navega para lá). Espelha o
 * `PersonaCreateModal`. Erros (incl. 422 de validação Meta e nome inválido)
 * aparecem inline; 409 = nome duplicado no tenant.
 */

import { useState } from 'react';

import { ApiError } from '../../lib/api-client';

import { CATEGORY_DESCRIPTIONS, categoryLabel } from './labels';
import { buildComponents } from './template-helpers';
import { useCreateTemplate } from './use-templates';

import type { TemplateCategory } from './types';

const CATEGORIES: TemplateCategory[] = ['authentication', 'utility', 'marketing'];
const NAME_PATTERN = /^[a-z0-9_]+$/;

interface TemplateCreateModalProps {
  onClose: () => void;
  onCreated: (id: string) => void;
}

export function TemplateCreateModal({ onClose, onCreated }: TemplateCreateModalProps): JSX.Element {
  const [name, setName] = useState('');
  const [category, setCategory] = useState<TemplateCategory>('utility');
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const mutation = useCreateTemplate();

  const nameValid = NAME_PATTERN.test(name);
  const bodyValid = body.trim().length > 0;
  const canSubmit = nameValid && bodyValid && !mutation.isPending;

  const handleCreate = (): void => {
    if (!canSubmit) return;
    setError(null);
    mutation.mutate(
      {
        name,
        language: 'pt_BR',
        category,
        components: buildComponents({ header: '', body, footer: '', buttons: [] }),
      },
      {
        onError: (err) => {
          if (err instanceof ApiError && err.status === 409) {
            setError('Já existe um template com esse nome neste tenant.');
            return;
          }
          if (err instanceof ApiError && (err.status === 422 || err.status === 400)) {
            setError(err.message.length > 0 ? err.message : 'Validação recusada pelo backend.');
            return;
          }
          setError('Não foi possível criar o template. Tente novamente.');
        },
        onSuccess: (created) => onCreated(created.id),
      },
    );
  };

  return (
    <div className="mt-modal-overlay" role="dialog" aria-modal="true" aria-label="Novo template">
      <div className="mt-modal">
        <h2 className="mt-modal__title">Novo template</h2>
        <p className="mt-modal__sub">
          Cria um rascunho. O ajuste fino e a submissão à Meta acontecem no editor.
        </p>

        {error !== null && (
          <div className="mt-banner mt-banner--danger" role="alert">
            <span>{error}</span>
          </div>
        )}

        <div className="mt-field">
          <span className="mt-field__label">Nome (lowercase, números, underscore)</span>
          <input
            className="mt-input"
            value={name}
            placeholder="ex: confirmacao_visita_tecnica"
            onChange={(e) => setName(e.target.value)}
          />
          {name.length > 0 && !nameValid && (
            <span className="mt-field__hint mt-field__hint--error">
              Use apenas letras minúsculas, números e underscore.
            </span>
          )}
        </div>

        <div className="mt-field">
          <span className="mt-field__label">Categoria</span>
          <div className="mt-radio-row">
            {CATEGORIES.map((c) => (
              <label key={c} className={`mt-radio${category === c ? ' mt-radio--selected' : ''}`}>
                <input
                  type="radio"
                  name="mt-create-category"
                  checked={category === c}
                  onChange={() => setCategory(c)}
                />
                <span className="mt-radio__main">
                  <span className="mt-radio__label">{categoryLabel(c)}</span>
                  <span className="mt-radio__desc">{CATEGORY_DESCRIPTIONS[c]}</span>
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="mt-field">
          <span className="mt-field__label">Body (obrigatório · variáveis {`{{1}}, {{2}}…`})</span>
          <textarea
            className="mt-textarea"
            rows={3}
            value={body}
            placeholder="Olá {{1}}, ..."
            onChange={(e) => setBody(e.target.value)}
          />
        </div>

        <div className="mt-actions">
          <button type="button" className="mt-btn mt-btn--ghost" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="mt-btn mt-btn--primary"
            disabled={!canSubmit}
            onClick={handleCreate}
          >
            Criar rascunho
          </button>
        </div>
      </div>
    </div>
  );
}
